import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { LogOut, RefreshCw, Phone, MapPin, Clock, Check, X, ChefHat, Bike, PackageCheck, Loader2 } from "lucide-react";

const STATUS_CONFIG = {
    pending:    { label: "PENDING",    color: "#ff9f1c", actions: ["accept", "reject"] },
    accepted:   { label: "ACCEPTED",   color: "#39ff8f", actions: ["preparing"] },
    preparing:  { label: "PREPARING",  color: "#ff9f1c", actions: ["on_the_way"] },
    on_the_way: { label: "ON THE WAY", color: "#00f0ff", actions: ["delivered"] },
    delivered:  { label: "DELIVERED",  color: "#39ff8f", actions: [] },
    rejected:   { label: "REJECTED",   color: "#ff2d4f", actions: [] },
    cancelled:  { label: "CANCELLED",  color: "#888",    actions: [] },
};

const ACTION_LABELS = {
    accept:     { label: "Accept",     icon: Check,         color: "#39ff8f", to: "accepted" },
    reject:     { label: "Reject",     icon: X,             color: "#ff2d4f", to: "rejected" },
    preparing:  { label: "Preparing",  icon: ChefHat,       color: "#ff9f1c", to: "preparing" },
    on_the_way: { label: "On the way", icon: Bike,          color: "#00f0ff", to: "on_the_way" },
    delivered:  { label: "Delivered",  icon: PackageCheck,  color: "#39ff8f", to: "delivered" },
};

// Audio "ding" notification using Web Audio API
const playOrderSound = () => {
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const note = (freq, start, dur) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = "sine";
            osc.frequency.value = freq;
            gain.gain.setValueAtTime(0.0001, ctx.currentTime + start);
            gain.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + start + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + dur);
            osc.connect(gain).connect(ctx.destination);
            osc.start(ctx.currentTime + start);
            osc.stop(ctx.currentTime + start + dur);
        };
        note(880, 0, 0.18);
        note(1320, 0.18, 0.22);
    } catch {}
};

export const StoreDashboard = () => {
    const navigate = useNavigate();
    const { user, login, logout } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loginLoading, setLoginLoading] = useState(false);
    const [orders, setOrders] = useState([]);
    const [storeData, setStoreData] = useState(null);
    const [filter, setFilter] = useState("active");
    const [refreshing, setRefreshing] = useState(false);

    const isStoreOwner = user?.role === "store_owner" || user?.role === "admin";

    const fetchData = useCallback(async () => {
        if (!isStoreOwner) return;
        setRefreshing(true);
        try {
            const [meRes, ordersRes] = await Promise.all([
                api.get("/store/me"),
                api.get("/store/orders"),
            ]);
            setStoreData(meRes.data.store);
            setOrders(ordersRes.data || []);
        } catch (err) {
            toast.error("Σφάλμα φόρτωσης παραγγελιών");
        } finally {
            setRefreshing(false);
        }
    }, [isStoreOwner]);

    useEffect(() => {
        if (isStoreOwner) fetchData();
    }, [isStoreOwner, fetchData]);

    // Auto-refresh every 15 seconds (fallback poll)
    useEffect(() => {
        if (!isStoreOwner) return;
        const id = setInterval(fetchData, 15000);
        return () => clearInterval(id);
    }, [isStoreOwner, fetchData]);

    // WebSocket — real-time order push
    useEffect(() => {
        if (!isStoreOwner) return;
        const token = localStorage.getItem("sh_token");
        if (!token) return;
        const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
        const ws = new WebSocket(`${base}/api/ws/store?token=${encodeURIComponent(token)}`);
        ws.onmessage = (ev) => {
            try {
                const msg = JSON.parse(ev.data);
                if (msg.type === "new_order") {
                    setOrders((prev) => [msg.order, ...prev.filter((o) => o.id !== msg.order.id)]);
                    playOrderSound();
                    toast.success(`Νέα παραγγελία από ${msg.order.customer_name}!`, { duration: 4000 });
                    if ("Notification" in window && Notification.permission === "granted") {
                        new Notification("Νέα παραγγελία 🔔", { body: `${msg.order.customer_name} — €${msg.order.total.toFixed(2)}` });
                    }
                } else if (msg.type === "order_updated") {
                    setOrders((prev) => prev.map((o) => (o.id === msg.order.id ? msg.order : o)));
                }
            } catch {}
        };
        ws.onerror = () => {};
        return () => { try { ws.close(); } catch {} };
    }, [isStoreOwner]);

    // Request browser notification permission once
    useEffect(() => {
        if (isStoreOwner && "Notification" in window && Notification.permission === "default") {
            Notification.requestPermission();
        }
    }, [isStoreOwner]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoginLoading(true);
        try {
            const u = await login(email.trim(), password);
            if (u.role !== "store_owner" && u.role !== "admin") {
                toast.error("Μόνο store accounts μπορούν να συνδεθούν εδώ.");
                await logout();
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Σφάλμα σύνδεσης");
        } finally {
            setLoginLoading(false);
        }
    };

    const updateStatus = async (orderId, newStatus) => {
        try {
            await api.patch(`/store/orders/${orderId}/status`, { status: newStatus });
            toast.success(`Status → ${newStatus}`);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Σφάλμα");
        }
    };

    if (!user || !isStoreOwner) {
        return (
            <div className="cyber-bg cyber-grid scanlines min-h-screen flex items-center justify-center p-4 text-white">
                <div className="w-full max-w-sm" data-testid="store-login">
                    <div className="text-center mb-6">
                        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--neon-green)]">// store_portal</p>
                        <h1 className="headline text-3xl mt-1">Σύνδεση Καταστήματος</h1>
                        <p className="text-xs text-white/50 mt-2">Διαχείριση παραγγελιών για το store σου.</p>
                    </div>
                    <form onSubmit={handleLogin} className="space-y-3">
                        <input
                            type="email"
                            required
                            data-testid="store-login-email"
                            placeholder="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full glass border-2 border-white/15 px-5 h-14 text-base rounded-xl focus:outline-none focus:border-[var(--neon-green)] placeholder:text-white/40"
                        />
                        <input
                            type="password"
                            required
                            data-testid="store-login-password"
                            placeholder="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full glass border-2 border-white/15 px-5 h-14 text-base rounded-xl focus:outline-none focus:border-[var(--neon-green)] placeholder:text-white/40"
                        />
                        <button
                            type="submit"
                            disabled={loginLoading}
                            data-testid="store-login-submit"
                            className="neon-btn w-full h-12 rounded-full font-mono uppercase tracking-widest text-xs font-bold flex items-center justify-center gap-2"
                        >
                            {loginLoading ? <Loader2 className="animate-spin" size={14} /> : null}
                            Sign in
                        </button>
                    </form>
                    <p className="text-[10px] font-mono text-white/30 text-center mt-6">
                        Demo: store@grillbros.gr / Store123!
                    </p>
                    <button onClick={() => navigate("/")} className="block mx-auto mt-4 text-xs font-mono uppercase tracking-widest text-white/40 hover:text-white">
                        ← Back to map
                    </button>
                </div>
            </div>
        );
    }

    const filtered = orders.filter((o) => {
        if (filter === "active") return !["delivered", "rejected", "cancelled"].includes(o.status);
        if (filter === "done")   return ["delivered", "rejected", "cancelled"].includes(o.status);
        return true;
    });

    return (
        <div className="cyber-bg cyber-grid min-h-screen text-white pb-12" data-testid="store-dashboard">
            {/* Header */}
            <header className="sticky top-0 z-30 glass border-b border-[var(--neon-green)]/30 px-4 py-3 flex items-center gap-3">
                {storeData?.logo_url && <img src={storeData.logo_url} alt="logo" className="h-9 w-9 rounded-full ring-2 ring-[var(--neon-green)]/40" />}
                <div className="flex-1 min-w-0">
                    <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-[var(--neon-green)]">store_portal</p>
                    <h1 className="font-heading uppercase text-base leading-none truncate">{storeData?.name || "Store"}</h1>
                </div>
                <button data-testid="refresh-btn" onClick={fetchData} className="h-9 w-9 grid place-items-center rounded-full bg-white/5 hover:bg-white/10 transition" title="refresh">
                    <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
                </button>
                <button data-testid="logout-btn" onClick={logout} className="h-9 w-9 grid place-items-center rounded-full bg-white/5 hover:bg-[var(--neon-red)]/20 transition" title="logout">
                    <LogOut size={14} />
                </button>
            </header>

            {/* Filter tabs */}
            <div className="sticky top-[57px] z-20 glass-neon border-b border-white/10 px-4 py-2.5 flex gap-2">
                {[
                    { id: "active", label: `Active (${orders.filter(o => !["delivered", "rejected", "cancelled"].includes(o.status)).length})` },
                    { id: "done",   label: "History" },
                    { id: "all",    label: "All" },
                ].map((t) => (
                    <button
                        key={t.id}
                        data-testid={`filter-${t.id}`}
                        onClick={() => setFilter(t.id)}
                        className={`px-3 h-8 rounded-full text-[10px] font-mono uppercase tracking-widest transition ${
                            filter === t.id ? "bg-[var(--neon-green)] text-black font-bold" : "bg-white/5 text-white/70 hover:text-white"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Order list */}
            <div className="px-4 py-4 space-y-3 max-w-2xl mx-auto">
                {filtered.length === 0 ? (
                    <div className="text-center py-20 text-white/40 text-sm">
                        <p className="font-mono text-xs uppercase tracking-widest">// no_orders</p>
                    </div>
                ) : (
                    filtered.map((o) => <OrderCard key={o.id} order={o} onUpdate={updateStatus} />)
                )}
            </div>
        </div>
    );
};

const OrderCard = ({ order, onUpdate }) => {
    const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
    const created = new Date(order.created_at);
    const ago = Math.floor((Date.now() - created.getTime()) / 60000);

    return (
        <article
            data-testid={`order-card-${order.id}`}
            className="glass border rounded-2xl p-4 transition"
            style={{ borderColor: cfg.color + "55", boxShadow: `0 0 20px ${cfg.color}15` }}
        >
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
                <div>
                    <p className="font-mono text-[10px] text-white/40">#{order.id.slice(0, 8).toUpperCase()}</p>
                    <h3 className="font-heading uppercase text-base truncate">{order.customer_name}</h3>
                </div>
                <span
                    data-testid={`order-status-${order.id}`}
                    className="px-2.5 py-1 rounded-full font-mono text-[9px] uppercase tracking-widest font-bold border"
                    style={{ color: cfg.color, borderColor: cfg.color + "66", background: cfg.color + "10" }}
                >
                    {cfg.label}
                </span>
            </div>

            {/* Customer info */}
            <div className="space-y-1.5 text-sm mb-3">
                <a href={`tel:${order.phone}`} className="flex items-center gap-2 text-white/80 hover:text-[var(--neon-green)] transition">
                    <Phone size={13} className="text-white/40" />
                    <span className="font-mono">{order.phone}</span>
                </a>
                <div className="flex items-start gap-2 text-white/80">
                    <MapPin size={13} className="text-white/40 mt-0.5 shrink-0" />
                    <span>{order.address}</span>
                </div>
                <div className="flex items-center gap-2 text-white/50 text-xs">
                    <Clock size={11} />
                    <span>{ago < 1 ? "just now" : `${ago}m ago`}</span>
                    <span className="ml-auto font-mono uppercase text-[10px]">{order.payment_method}</span>
                </div>
            </div>

            {/* Items */}
            <div className="bg-black/30 rounded-lg p-3 mb-3 space-y-1.5">
                {order.items.map((it, i) => (
                    <div key={i} className="flex justify-between text-sm">
                        <span className="text-white/85 truncate mr-2">{it.quantity}× {it.name}</span>
                        <span className="font-mono text-white/60">€{(it.price * it.quantity).toFixed(2)}</span>
                    </div>
                ))}
                <div className="border-t border-white/10 pt-1.5 flex justify-between text-sm font-bold">
                    <span>Total</span>
                    <span className="font-mono neon-text-green">€{order.total.toFixed(2)}</span>
                </div>
            </div>

            {/* Notes */}
            {order.notes && (
                <p className="text-xs text-white/60 italic mb-3 px-2 py-1.5 rounded bg-white/5">📝 {order.notes}</p>
            )}

            {/* Actions */}
            {cfg.actions.length > 0 && (
                <div className="flex gap-2">
                    {cfg.actions.map((act) => {
                        const a = ACTION_LABELS[act];
                        const Icon = a.icon;
                        return (
                            <button
                                key={act}
                                data-testid={`action-${act}-${order.id}`}
                                onClick={() => onUpdate(order.id, a.to)}
                                className="flex-1 h-11 rounded-full border-2 font-mono uppercase tracking-widest text-[10px] font-bold flex items-center justify-center gap-1.5 transition active:scale-95 hover:bg-white/5"
                                style={{ borderColor: a.color, color: a.color }}
                            >
                                <Icon size={13} strokeWidth={2.5} />
                                {a.label}
                            </button>
                        );
                    })}
                </div>
            )}
        </article>
    );
};
